/**
 * A recorded voice sample between the recorder and the server (ADR-0062): the *take*. It is held
 * in the tab's memory — never at rest on the device (ADR-0048) — with the capture id it got when
 * it was recorded: every send of it, the first and each *Try again*, names the same capture, so
 * the server never stores it twice. A dialog that only collects hands the take to whoever opened
 * it; it is then theirs to hold, and to send once there is a subject (`sendVoiceSampleTake`).
 * Pure but for the id it is given.
 */

import type { RecordingFileProblem } from '@/components/humint/questioning/recording-types'
import type {
  VoiceCounting,
  VoiceMicrophoneProblem,
  VoiceTakeEnding,
  VoiceTakeSummary,
} from '@/components/humint/voice/voice-types'
import type { RecordingExtension } from '@/services/api/humint-recordings'
import { MAX_VOICE_SAMPLE_BYTES } from '@/services/api/humint-voice'
import { asPlayable } from '@/utils/audio-playback'
import { randomUuid } from '../mappers/enrol'
import type { RecordedAudio } from '../recording/recorder-controller'
import { intakeOfFile, intakeOfRecorded } from '../recording/recording-intake'
import type { VoiceSample } from './voice-sample-session'

export interface VoiceSampleTake {
  readonly fileName?: string
  /** The capture's id, also its idempotency key: every send repeats it. */
  readonly captureId: string
  /** The recording as the browser's recorder wrote it, when it started and how long it is. */
  readonly audio: RecordedAudio
  /** The container it was recorded in, as the upload's file extension. */
  readonly extension: RecordingExtension
  /** Net speech counted while it recorded: an estimate, the server measures the audio itself. */
  readonly speechMs: number
  readonly counting: VoiceCounting
  readonly ending: VoiceTakeEnding
  /** The level over the recording, 0..1 per bar. */
  readonly waveform: readonly number[]
}

/** What became of a recording that ended: a take, or why there is none. */
export type VoiceSampleIntake =
  | { kind: 'taken'; take: VoiceSampleTake }
  | { kind: 'refused'; problem: VoiceMicrophoneProblem }

/**
 * Take a recording that has ended, under a fresh capture id that stays with it. One with nothing
 * in it, or in a container the server does not take, is no take: the dialog says so and offers to
 * record again.
 */
export function takeOfSample(
  sample: VoiceSample,
  newId: () => string = randomUuid
): VoiceSampleIntake {
  const intake = intakeOfRecorded(sample.audio)
  if (intake.kind === 'refused') {
    return { kind: 'refused', problem: intake.refusal === 'empty' ? 'empty' : 'unsupported' }
  }
  const { audio, speechMs, counting, ending, waveform } = sample
  const { extension } = intake.source
  const take = { captureId: newId(), audio, extension, speechMs, counting, ending, waveform }
  return { kind: 'taken', take }
}

/** What the screens show of a take. */
export function takeSummary(take: VoiceSampleTake): VoiceTakeSummary {
  const { speechMs, counting, ending, waveform, fileName } = take
  return {
    durationMs: take.audio.durationMs,
    speechMs,
    counting,
    ending,
    waveform,
    ...(fileName ? { fileName } : {}),
  }
}

/** Imported files are measured by the existing server pipeline; no client speech estimate is invented. */
export function takeOfVoiceFile(
  file: File
): { kind: 'taken'; take: VoiceSampleTake } | { kind: 'refused'; problem: RecordingFileProblem } {
  if (file.size > MAX_VOICE_SAMPLE_BYTES) return { kind: 'refused', problem: 'too_large' }
  const intake = intakeOfFile(file)
  if (intake.kind === 'refused') return { kind: 'refused', problem: intake.refusal }
  return {
    kind: 'taken',
    take: {
      captureId: randomUuid(),
      fileName: file.name,
      extension: intake.source.extension,
      audio: {
        blob: asPlayable(file, file.name),
        startedAt: intake.source.capturedAt,
        durationMs: 0,
        interrupted: false,
      },
      speechMs: 0,
      counting: 'time',
      ending: 'stopped',
      waveform: [],
    },
  }
}
